<script setup lang="ts">
import type { HelperBtns } from "./types";
import { loadLeafletCDN } from "@/leaflet/loadLeafletCDN";
import { Loader2, LocateFixed, MapPinCheckInside } from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { onMounted, onUnmounted, ref } from "vue";
import { toast } from '@/ui-plus/sonner';
import { geocode } from "./geocode";
import { ipwhois } from "@/ui-plus/geolocation-selection-map/ipwhois";
// import { searchOpenStreetMap } from "./searchOpenStreetMap";

import { calculateDistance } from "@lib/utils/calculateDistance";
import Badge from "@/components/ui/badge/Badge.vue";

const props = defineProps<{
	address: string;
	country: string;
	lat?: number;
	lng?: number;
	btnLabel?: string;
}>();

const emit = defineEmits<{
	(e: 'done', result: { lat: number, lng: number }): void
}>();

const handleKeyPress = (event: KeyboardEvent) => {
	console.log("handleKeyPress event", event);
	if (event.key === 'Enter') {
		emitDone();
	}
};

const randomId = `map-${Math.random().toString(36).substring(2, 15)}`;
const helperBtns = ref<HelperBtns[]>([]);
let mapView: L.Map | null = null;
// let marker: L.Marker | null = null;
let circleRef: L.Circle | null = null;

const isConfirmDisabled = ref(true);
const isOutOfRange = ref(false);
const isInvalidAddress = ref(false);
const isGpsLoading = ref(false);

const mapMoveListener = () => {
	// marker?.setLatLng([mapView?.getCenter().lat ?? 0, mapView?.getCenter().lng ?? 0])
	validateMarkerPosition(mapView?.getCenter().lat ?? 0, mapView?.getCenter().lng ?? 0)
}

onUnmounted(() => {
	mapView?.off('move', mapMoveListener)
	mapView?.off('resize', mapMoveListener)
	document.removeEventListener('keypress', handleKeyPress);
})

function validateMarkerPosition(lat: number, lng: number) {
	if (circleRef) {
		let distance = 0
		try {
			distance = calculateDistance(
				{ lat, lng },
				{ lat: circleRef.getLatLng().lat, lng: circleRef.getLatLng().lng }
			)
		} catch (error) {
			distance = 1 // make it red
		}
		if (distance > 0.3) {
			circleRef.setStyle({ color: 'red' });
			isOutOfRange.value = true
		} else {
			circleRef.setStyle({ color: 'green' });
			isOutOfRange.value = false
		}
	}
}
function setCenter(lat: number, lng: number) {
	mapView?.setView([Number(lat), Number(lng)]);
	// marker?.setLatLng([lat, lng])
	validateMarkerPosition(lat, lng)
}

onMounted(async () => {
	const loaded = await loadLeafletCDN();
	if (!loaded) {
		return;
	}

	mapView = window.L.map(randomId, {
		// @ts-ignore
		gestureHandling: true,
	});
	// marker = window.L.marker([0, 0]).addTo(mapView)
	// marker.setOpacity(1)
	mapView?.on('move', mapMoveListener)
	mapView?.on('resize', mapMoveListener)
	mapView.attributionControl.setPrefix(false);
	mapView.addLayer(
		window.L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
			maxZoom: 19,
			attribution: '<a target="_blank" rel="noopener noreferrer" href="https://osm.org/copyright">© OpenStreetMap</a>',
			// @ts-ignore
			referrerPolicy: 'strict-origin-when-cross-origin'
		})
	);

	mapView.fitWorld(); // no need to load unnecessary tiles
	mapView.setZoom(18);
	loadHelperBtns();
	// mapView?.on('load', () => {
	// 	console.log("!!!mapView loaded", mapView?.getCenter());
	// });
	mapView?.whenReady(() => {
		isConfirmDisabled.value = false;
	});
	document.addEventListener('keypress', handleKeyPress);
});


async function handleGpsClick() {
	isGpsLoading.value = true;
	await new Promise((resolve) => setTimeout(resolve, 500));
	if ("geolocation" in navigator) {
		navigator.geolocation.getCurrentPosition(
			(position) => {
				isGpsLoading.value = false;
				mapView?.setView([position.coords.latitude, position.coords.longitude], 18);
				// setCenter(position.coords.latitude, position.coords.longitude)

				// Check if Current Location already exists in helperBtns
				const currentLocationExists = helperBtns.value.some(btn => btn.type === "GPS");
				if (currentLocationExists) {
					// Update existing Current Location coordinates
					helperBtns.value = helperBtns.value.map(btn => {
						if (btn.label === "Current Location") {
							return {
								...btn,
								lat: position.coords.latitude,
								lng: position.coords.longitude
							};
						}
						return btn;
					});
				} else {
					// Add new Current Location to start of array
					helperBtns.value = [{
						label: "Current Location",
						lat: position.coords.latitude,
						lng: position.coords.longitude,
						type: 'GPS'
					}, ...helperBtns.value];
				}
			},
			(error) => {
				console.log("error", error);
				toast.error("Geolocation error: check permissions and try again");
				isGpsLoading.value = false;
			},
			{
				enableHighAccuracy: true,
				maximumAge: 0,
				timeout: 5000
			}
		);
	} else {
		toast.error("Geolocation is not supported by your web browser.");
		isGpsLoading.value = false;
	}
}

async function loadHelperBtns() {
	const gecodeResult = await geocode(props.address, props.country?.toLowerCase())
	const firstResult = gecodeResult.data?.results?.[0]
	if (gecodeResult && gecodeResult.error) {
		// isOutOfRange.value = true; 
		// // no need to force user about accurate location we already check address and lat,lng distance in server side
		mapView?.fitWorld();
		toast.error(gecodeResult.error.message || "Server error: please try again later", {
			action: {
				label: 'Use device location📍',
				onClick: () => handleGpsClick(),
			},
		});
		const ipWhoisResult = await ipwhois()
		mapView?.setView([ipWhoisResult?.latitude ?? 0, ipWhoisResult?.longitude ?? 0], 12);
	} else if (firstResult
		// && !firstResult.partial_match
	) {
		const lat = firstResult.geometry.location.lat;
		const lng = firstResult.geometry.location.lng;

		// Remove existing circle if it exists
		if (circleRef) {
			mapView?.removeLayer(circleRef);
		}

		// Create new circle with 300m radius
		circleRef = window.L.circle([lat, lng], {
			radius: 300,
			color: 'green',
			fillOpacity: 0,
			weight: 5,
		}).addTo(mapView!);

		helperBtns.value = [
			{
				label: firstResult.formatted_address || props.address,
				lat: firstResult.geometry.location.lat,
				lng: firstResult.geometry.location.lng,
				type: 'geocode' as const
			},
		]
		if (props.lat && props.lng) {
			setCenter(props.lat, props.lng)
			if (helperBtns.value[0] && helperBtns.value[0].lat && helperBtns.value[0].lng) {
				const distance = calculateDistance(
					{ lat: props.lat, lng: props.lng },
					{ lat: helperBtns.value[0].lat, lng: helperBtns.value[0].lng }
				)
				if (distance > 0.3) {
					setCenter(helperBtns.value[0].lat!, helperBtns.value[0].lng!)
				}
			}
		} else {
			setCenter(firstResult.geometry.location.lat, firstResult.geometry.location.lng)
		}
		// } else if (firstResult && firstResult.partial_match) {
		// 	isInvalidAddress.value = true
		// 	isOutOfRange.value = true
		// 	setCenter(firstResult.geometry.location.lat, firstResult.geometry.location.lng)
	} else {
		// isOutOfRange.value = true; 
		// // no need to force user about accurate location we already check address and lat,lng distance in server side
		mapView?.fitWorld();
		toast.error("No results found for address: " + props.address, {
			action: {
				label: 'Use device location📍',
				onClick: () => handleGpsClick(),
			},
		});
		const ipWhoisResult = await ipwhois()
		mapView?.setView([ipWhoisResult?.latitude ?? 0, ipWhoisResult?.longitude ?? 0], 12);
		// handleGpsClick();
		// const openStreetMapItems = await searchOpenStreetMap(props.address, props.country?.toLowerCase())
		// const ipwho = await ipwhois()
		// helperBtns.value = [
		// 	...gecodeResult.data.results.map(item => ({
		// 		label: item.formatted_address || props.address,
		// 		lat: item.geometry.location.lat,
		// 		lng: item.geometry.location.lng,
		// 		type: 'geocode' as const
		// 	})),
		// 	...openStreetMapItems.map(item => ({
		// 		label: item.label,
		// 		lat: item.lat,
		// 		lng: item.lng,
		// 		type: 'openstreetmap' as const
		// 	})),
		// ]
		// if (helperBtns.value.length === 0) {
		// 	if (ipwho.postal || ipwho.city || ipwho.country || ipwho.region || ipwho.ip) {
		// 		helperBtns.value.push({
		// 			label: ipwho.postal || ipwho.city || ipwho.country || ipwho.region || ipwho.ip,
		// 			lat: ipwho.latitude ?? 0,
		// 			lng: ipwho.longitude ?? 0,
		// 			type: 'ipwhois' as const
		// 		})
		// 	}
		// 	handleGpsClick()
		// }
	}
	// if (props.lat && props.lng) {
	// 	// remember last location
	// 	setCenter(props.lat, props.lng)
	// 	// but if the distance is greater than 0.3 km use geocode
	// 	if (helperBtns.value[0] && helperBtns.value[0].lat && helperBtns.value[0].lng) {
	// 		const distance = calculateDistance({ lat: props.lat, lng: props.lng }, { lat: helperBtns.value[0].lat, lng: helperBtns.value[0].lng })
	// 		if (distance > 0.3) {
	// 			setCenter(helperBtns.value[0].lat!, helperBtns.value[0].lng!)
	// 		}
	// 	}
	// } else if (helperBtns.value[0] && helperBtns.value[0].lat && helperBtns.value[0].lng) {
	// 	setCenter(helperBtns.value[0].lat, helperBtns.value[0].lng)
	// }
}

function emitDone() {
	if (isConfirmDisabled.value || isOutOfRange.value) {
		toast.error("Please select a valid location")
	} else {
		emit('done', { lat: mapView?.getCenter().lat ?? 0, lng: mapView?.getCenter().lng ?? 0 })
	}
}
</script>
<template>
	<div class="h-full geolocation-selection-map">
		<div class="flex h-full flex-col relative">
			<p class="address-value border p-1 text-sm font-extrabold shrink-0">
				{{ props.address || "" }}
				<Badge v-if="isInvalidAddress"
					   variant="destructive">Invalid</Badge>
			</p>


			<div :id="randomId"
				 class="relative flex-1 min-h-0">
				<div class="center-crosshair"></div>
				<Loader2 v-if="isConfirmDisabled"
						 class="animate-spin w-20 h-20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger as-child>
							<Button type="button"
									variant="outline"
									:disabled="isGpsLoading"
									class="rubberBand-animation absolute right-1 top-1 z-[1001]"
									@click="handleGpsClick">
								<Loader2 v-if="isGpsLoading"
										 class="animate-spin" />
								<LocateFixed v-else
											 class="" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="left">
							<p>Find Your Location</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
				<div
					 class="pointer-events-none absolute inset-0 top-0 left-0 w-full h-full dark:backdrop-hue-rotate-180 dark:backdrop-invert z-[1001]">
				</div>
			</div>
			<div
				 class="headShake-animation my-2 max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap shrink-0">

				<TooltipProvider v-for="(helperBtn, index) in helperBtns"
								 :key="index">
					<Tooltip>
						<TooltipTrigger as-child>
							<Button variant="outline"
									class="m-1 inline-block max-w-[200px] truncate"
									@click="setCenter(helperBtn.lat ?? 0, helperBtn.lng ?? 0)">
								{{ helperBtn.label }}
							</Button>
						</TooltipTrigger>
						<TooltipContent side="top"
										class="max-w-[200px] max-h-[200px] overflow-auto">
							<p>{{ helperBtn.type }}: {{ helperBtn.label }}</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</div>

			<div>
				<div v-if="isOutOfRange"
					 class="bg-destructive text-destructive-foreground p-2 text-sm font-medium shrink-0 no-scrollbar overflow-x-auto overflow-y-hidden whitespace-nowrap">
					⚠️ Current location is too far from the address. use btn above to find your address.
					<Button type="button"
							variant="outline"
							:disabled="isGpsLoading"
							@click="handleGpsClick">
						or use your device location📍
					</Button>
				</div>
				<Button v-else
						type="button"
						size="lg"
						:disabled="isConfirmDisabled || isOutOfRange"
						@click="emitDone"
						class="w-full text-lg font-bold">
					<Loader2 v-if="isConfirmDisabled"
							 class="animate-spin" />
					<MapPinCheckInside v-else />
					{{ btnLabel || "Confirm" }}
				</Button>
			</div>
		</div>
	</div>
</template>
<style>
.geolocation-selection-map .leaflet-marker-icon {
	filter: hue-rotate(150deg) saturate(1.5);
}
</style>
<style scoped>
.center-crosshair {
	position: absolute;
	top: 50%;
	left: 50%;
	opacity: 0.8;
	transform: translate(-50%, -35%);
	width: 40px;
	height: 40px;
	pointer-events: none;
	z-index: 1000;
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ea4335'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E");
	background-size: contain;
	background-repeat: no-repeat;
	background-position: center;
}
</style>
